<template>
  <div class="image-upload">
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        {{ label }}
      </label>
      
      <!-- Image preview -->
      <div v-if="previewUrl" class="mb-4">
        <img 
          :src="previewUrl" 
          :alt="label"
          class="w-32 h-32 object-cover rounded-lg border border-gray-300"
        />
      </div>
      
      <!-- Upload area -->
      <div 
        @click="triggerFileInput"
        @dragover.prevent
        @drop.prevent="handleDrop"
        class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
        :class="{ 'border-primary-500 bg-primary-50': isDragOver }"
      >
        <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <p class="mt-2 text-sm text-gray-600">
          <span class="font-medium text-primary-600 hover:text-primary-500">Cliquez pour uploader</span>
          ou glissez-déposez
        </p>
        <p class="text-xs text-gray-500">PNG, JPG, JPEG jusqu'à 10MB</p>
      </div>
      
      <!-- Hidden file input -->
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        @change="handleFileSelect"
        class="hidden"
      />
      
      <!-- Upload progress -->
      <div v-if="uploading" class="mt-4">
        <div class="bg-gray-200 rounded-full h-2">
          <div 
            class="bg-primary-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: uploadProgress + '%' }"
          ></div>
        </div>
        <p class="text-sm text-gray-600 mt-1">Upload en cours... {{ uploadProgress }}%</p>
      </div>
      
      <!-- Error message -->
      <div v-if="error" class="mt-2 text-sm text-red-600">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageUpload',
  props: {
    label: {
      type: String,
      default: 'Image'
    },
    value: {
      type: String,
      default: ''
    },
    folder: {
      type: String,
      required: true
    },
    entryId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      previewUrl: this.value,
      uploading: false,
      uploadProgress: 0,
      error: '',
      isDragOver: false
    }
  },
  watch: {
    value(newVal) {
      this.previewUrl = newVal
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.uploadFile(file)
      }
    },
    
    handleDrop(event) {
      this.isDragOver = false
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.uploadFile(file)
      }
    },
    
    async uploadFile(file) {
      // Validation
      if (!file.type.startsWith('image/')) {
        this.error = 'Veuillez sélectionner un fichier image valide'
        return
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB
        this.error = 'Le fichier est trop volumineux (max 10MB)'
        return
      }
      
      this.error = ''
      this.uploading = true
      this.uploadProgress = 0
      
      try {
        // Préparer les données pour l'upload
        const formData = new FormData()
        formData.append('file', file)
        
        // Simuler la progression
        const progressInterval = setInterval(() => {
          if (this.uploadProgress < 90) {
            this.uploadProgress += 10
          }
        }, 100)
        
        // Appel API réel pour l'upload
        const url = this.entryId 
          ? `/api/v1/upload/${this.folder}?entry_id=${this.entryId}`
          : `/api/v1/upload/${this.folder}`
        
        const response = await fetch(url, {
          method: 'POST',
          body: formData
        })
        
        clearInterval(progressInterval)
        this.uploadProgress = 100
        
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Erreur lors de l\'upload')
        }
        
        const result = await response.json()
        
        // Mettre à jour la preview
        this.previewUrl = URL.createObjectURL(file)
        
        // Émettre l'événement avec l'URL de l'image
        this.$emit('input', result.url)
        this.$emit('uploaded', {
          url: result.url,
          fileName: result.filename,
          originalName: result.original_name,
          size: result.size
        })
        
      } catch (error) {
        this.error = 'Erreur lors de l\'upload: ' + error.message
      } finally {
        this.uploading = false
        this.uploadProgress = 0
      }
    }
  }
}
</script>

<style scoped>
.image-upload {
  @apply w-full;
}
</style>
